import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useSearchParams } from "react-router-dom";
import { changeIndex, emptyGallery, setGallery } from "../redux/gallerySlice";
import { useEffect, useRef } from "react";
import ZoomableImage from "../components/ZoomableImage";
import Loader from "../components/Loader";
import { getCloudinaryDisplayUrl } from "../utils/getCloudinaryDisplayUrl";

const PhotosGallery = () => {
    const { array, indexShowed, activityId } = useSelector(state => state.gallery);

    // activityId e indice foto sono anche nei query param: al refresh Redux si
    // azzera e la galleria viene ricostruita da qui (fetch sotto)
    const [searchParams, setSearchParams] = useSearchParams();
    const urlActivityId = searchParams.get('activityId');

    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Ricostruzione post-refresh: Redux vuoto ma activityId nell'URL → rifetcho
    // la galleria dell'attività. `useRef` per non ripartire in StrictMode.
    const isRebuilding = useRef(false);
    useEffect(() => {
        if (activityId) return;
        if (!urlActivityId) {
            // Nessuna informazione né in Redux né nell'URL: torno alla home
            // (il vecchio fallback /handle-activities era una pagina admin)
            navigate('/');
            return;
        }
        if (isRebuilding.current) return;
        isRebuilding.current = true;

        const rebuildGallery = async () => {
            try {
                const token = localStorage.getItem('ticino-wild-token');
                const url = `${process.env.REACT_APP_SERVER_BASE_URL}/activities/get-activity-user/${urlActivityId}`;
                const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
                const result = await response.json();
                if (!response.ok) throw new Error(result.message);

                const gallery = result.payload?.gallery;
                if (!gallery || gallery.length === 0) throw new Error('Galleria vuota');

                const requestedPhoto = parseInt(searchParams.get('photo') || '0', 10);
                const firstShowed = Number.isNaN(requestedPhoto)
                    ? 0
                    : Math.min(Math.max(requestedPhoto, 0), gallery.length - 1);

                dispatch(setGallery({ array: gallery, firstShowed, activityId: urlActivityId }));
            } catch (error) {
                // Attività non trovata o fetch fallito: riporto alla pagina di dettaglio
                console.error('Errore nella ricostruzione della galleria:', error);
                navigate(`/activity-details?id=${urlActivityId}`, { replace: true });
            } finally {
                isRebuilding.current = false;
            }
        };
        rebuildGallery();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activityId, urlActivityId])

    // Gestisco il ritorno alla pagina ActivityDetails
    const handleBackLink = () => {
        const backId = activityId || urlActivityId;
        if (backId) {
            navigate(`/activity-details?id=${backId}`);
        } else {
            navigate('/');
        }
        dispatch(emptyGallery());
    }

    // Cambio foto dalla miniatura: aggiorno anche il query param `photo`,
    // così il refresh riapre la galleria sulla foto giusta
    const handleThumbnailClick = (index) => {
        dispatch(changeIndex(index));
        setSearchParams(
            { activityId: activityId || urlActivityId, photo: String(index) },
            { replace: true }
        );
    }

    return (
        // Solo padding verticale: quello orizzontale (px-4) lo mette già MainContainer,
        // e sommarli raddoppiava i margini ai lati dell'immagine
        <div className="bg-neutral-100 flex flex-col items-center py-4 overflow-y-auto">
            <div className="rounded-xl flex flex-col gap-4 w-full max-w-[1400px]">

                {/* Loader durante la ricostruzione post-refresh */}
                {
                    !array &&
                    <div className="w-full min-h-[300px] flex items-center justify-center">
                        <Loader />
                    </div>
                }

                {/* Image */}
                {
                    array &&
                    <div className="relative w-full flex justify-center">
                        {/* Close icon — z-10 per restare sopra l'immagine anche da ingrandita.
                            Cerchio bianco a filo dell'icona, senza sbordi */}
                        <div className={`absolute top-2 right-2 z-10 w-10 h-10 rounded-full bg-white flex items-center justify-center text-[#225424] cursor-pointer`} onClick={() => handleBackLink()}>
                            {/* mt: compensa la baseline alta dei glifi Flaticon */}
                            <i className="fi fi-rr-circle-xmark text-4xl leading-none mt-1.5"></i>
                        </div>

                        {/* Variante ridimensionata (w_1600): il secure_url originale può pesare
                            diversi MB e impiegava ~10s a caricare a ogni cambio foto.
                            zoomSrc (w_3200) viene caricata SOLO se l'utente zooma, per non
                            sgranare da ingrandita — upgrade progressivo dentro ZoomableImage. */}
                        <ZoomableImage
                            src={getCloudinaryDisplayUrl(array[indexShowed].secure_url)}
                            zoomSrc={getCloudinaryDisplayUrl(array[indexShowed].secure_url, 'w_3200,c_limit,q_auto,f_auto')}
                            alt={array[indexShowed].public_id || ''}
                        />
                    </div>
                }

                {/* Slide */}
                {
                    array &&
                    <div className="flex gap-2 p-2 rounded-xl mx-2 overflow-x-auto">
                        {
                            array.map((element, index) => {
                                return <div key={index} className="min-w-[100px] h-[100px] md:min-w-[200px] md:h-[200px] overflow-hidden"><img src={element.resizedUrl} alt={element.public_id} className="w-full h-full rounded-xl md:cursor-pointer" onClick={() => handleThumbnailClick(index)} /></div>
                            })
                        }
                    </div>
                }
            </div>
        </div>
    )
}

export default PhotosGallery
